// src/components/ProfileCompletionCard.tsx
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@/redux/hooks/hooks";
import { computeProfileCompletion, pickEmoji } from "@/utils/profileCompletion";
import ScriptWritingIcon from "@/assets/icons/authentication/skills/ScriptWritting.svg";
import VoiceOversIcon from "@/assets/icons/authentication/skills/VoiceOvers.svg";
import ActingIcon from "@/assets/icons/authentication/skills/Acting.svg";
import VideoGraphyIcon from "@/assets/icons/authentication/skills/VideoGraphy.svg";
import VideoEditingIcon from "@/assets/icons/authentication/skills/VideoEditing.svg";
import ModelingIcon from "@/assets/icons/authentication/skills/Modeling.svg";
import OthersPlusIcon from "@/assets/icons/authentication/skills/OthersPlus.svg";
import InstagramIcon from "@/assets/icons/authentication/linkSocial/Instagram.svg";
import LinkedInIcon from "@/assets/icons/authentication/linkSocial/LinkedIn.svg";
import TiktokIcon from "@/assets/icons/authentication/linkSocial/Tiktok.svg";
import BehanceIcon from "@/assets/icons/authentication/linkSocial/Behaance.svg";

import YoutubeIcon from "@/assets/icons/authentication/linkSocial/Youtube.svg";

import PhoneIcon from "@/assets/icons/authentication/PhoneIcon.svg";
import LocationIcon from "@/assets/icons/authentication/LocationIcon.svg";

import DslrImage from "@/assets/images/auth/recording-cor-setup/dslr.png";
import IphoneImage from "@/assets/images/auth/recording-cor-setup/iphone.png";
import AndroidImage from "@/assets/images/auth/recording-cor-setup/android.png";
import TripodImage from "@/assets/images/auth/recording-cor-setup/tripod.png";
import MicrophoneImage from "@/assets/images/auth/recording-cor-setup/microphone.png";
import NoiseControlImage from "@/assets/images/auth/recording-cor-setup/noise-control-room.png";
import ArtificialLightingImage from "@/assets/images/auth/recording-cor-setup/artificial-light.png";
import AestheticImage from "@/assets/images/auth/recording-cor-setup/aesthetic-bg.png";
import GreenScreenImage from "@/assets/images/auth/recording-cor-setup/gree-screen.png";
import PlusImage from "@/assets/images/auth/recording-cor-setup/plus.png";

function useAnimatedNumber(target: number, duration = 600) {
  const [value, setValue] = useState(target);

  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const from = value;

    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return value;
}

const SKILL_MAP: Record<string, { label: string; icon: React.ReactNode }> = {
  script_writing: {
    label: "Script Writing",
    icon: <ScriptWritingIcon />,
  },
  voice_overs: {
    label: "Voiceovers",
    icon: <VoiceOversIcon />,
  },
  acting: {
    label: "Acting",
    icon: <ActingIcon />,
  },
  videography_dop: {
    label: "Videography (DOP)",
    icon: <VideoGraphyIcon />,
  },
  video_editing: {
    label: "Video Editing",
    icon: <VideoEditingIcon />,
  },
  modelling: {
    label: "Modelling",
    icon: <ModelingIcon />,
  },
  others: {
    label: "Others",
    icon: <OthersPlusIcon />,
  },
};

const SOCIAL_ICON_MAP: Record<string, React.ReactNode> = {
  instagram: <InstagramIcon />,
  youtube: <YoutubeIcon />,
  tiktok: <TiktokIcon />,
  linkedin: <LinkedInIcon />,
  behance: <BehanceIcon />,
};

const CORE_SETUP_MAP: Record<string, { label: string; icon: string }> = {
  dslr: {
    label: "DSLR Camera",
    icon: DslrImage,
  },
  iphone: {
    label: "Iphone",
    icon: IphoneImage,
  },
  androidPhone: {
    label: "Android Phone",
    icon: AndroidImage,
  },
  tripod: {
    label: "Tripod / Stable mount",
    icon: TripodImage,
  },
  microphone: {
    label: "External microphone",
    icon: MicrophoneImage,
  },
  "noise-control": {
    label: "Quiet / noise-controlled room",
    icon: NoiseControlImage,
  },
  "artificial-lighting": {
    label: "Artificial lighting",
    icon: ArtificialLightingImage,
  },
  "green-screen": {
    label: "Green screen",
    icon: GreenScreenImage,
  },
  "aesthetic-bg": {
    label: "Aesthetic background",
    icon: AestheticImage,
  },
  others: {
    label: "Others",
    icon: PlusImage,
  },
};

function SectionTitle({ title }: { title: string }) {
  return (
    <p className="mb-2.5 text-center text-[10px] font-medium tracking-[-0.2px] text-[#A6A6A6]">
      {title}
    </p>
  );
}

function InfoRow({
  icon,
  text,
}: {
  icon: React.ReactNode | null;
  text: string;
}) {
  if (!text) return null;

  return (
    <div className="flex max-w-full items-center justify-center gap-1.5 text-[10px] font-medium tracking-[-0.2px] text-[#8F8F96]">
      {icon ? <span className="shrink-0">{icon}</span> : null}
      <span className="truncate">{text}</span>
    </div>
  );
}

function Chip({
  children,
  icon,
  className = "",
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-[#F5F5F7] px-2.5 py-1 text-[10px] font-medium text-[#121217] ${className}`}
    >
      {icon ? <span className="flex shrink-0 items-center">{icon}</span> : null}
      <span className="truncate">{children}</span>
    </span>
  );
}

function formatFollowers(value?: number | null) {
  if (value == null) return "";

  if (value >= 1000000) {
    const n = value / 1000000;
    return `${Number.isInteger(n) ? n : n.toFixed(1)}M`;
  }

  if (value >= 1000) {
    const n = value / 1000;
    return `${Number.isInteger(n) ? n : n.toFixed(1)}K`;
  }

  return `${value}`;
}

function getVideoSrc(video: any) {
  if (!video) return "";

  if (video.sourceType === "link") return video.linkUrl || "";
  if (video.file) return video.file.live_url || video.file.local_url || "";

  return "";
}

function cleanOtherNoise(value?: string | null) {
  if (!value) return "";

  return value
    .replace(/__others__\d+/gi, "")
    .replace(/others__\d+/gi, "")
    .replace(/__+/g, " ")
    .replace(/[_]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+,/g, ",")
    .replace(/,\s*,/g, ",")
    .trim();
}

function toTitleCase(value: string) {
  return value.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
  });
}

function getDisplayLocation(parts: Array<string | null | undefined>) {
  const cleaned = parts
    .map((part) => cleanOtherNoise(part))
    .map((part) => part.trim())
    .filter(Boolean);

  const unique = cleaned.filter(
    (part, index) => cleaned.indexOf(part) === index,
  );

  return unique.join(", ");
}

function getSocialDisplayLabel(social: {
  platform: string;
  label?: string;
  url?: string;
}) {
  if (social.platform === "custom") {
    return social.label || "Custom";
  }

  if (social.platform === "website") {
    return social.label || "Website";
  }

  return toTitleCase(social.platform);
}

function getPrioritySocials(socials: any[]) {
  const priorityOrder = [
    "instagram",
    "youtube",
    "tiktok",
    "linkedin",
    "facebook",
    "x",
    "behance",
    "website",
    "custom",
  ];

  return [...socials].sort((a, b) => {
    const aIndex = priorityOrder.indexOf(a.platform);
    const bIndex = priorityOrder.indexOf(b.platform);

    const safeA = aIndex === -1 ? 999 : aIndex;
    const safeB = bIndex === -1 ? 999 : bIndex;

    return safeA - safeB;
  });
}

function getCoreSetupLabel(item: { type: string; details?: string }) {
  const mapped = CORE_SETUP_MAP[item.type];
  if (!mapped) return cleanOtherNoise(item.type);

  if (item.type === "others" && item.details?.trim()) {
    return cleanOtherNoise(item.details);
  }

  return mapped.label;
}

function CoreSetupChip({ type, details }: { type: string; details?: string }) {
  const mapped = CORE_SETUP_MAP[type];
  const label = getCoreSetupLabel({ type, details });

  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-[#F5F5F7] px-2.5 py-1 text-[10px] font-medium text-[#121217]">
      {mapped?.icon ? (
        <img
          src={mapped.icon}
          alt={mapped.label}
          className="h-3.5 w-3.5 shrink-0 object-contain"
        />
      ) : null}
      <span className="truncate">{label}</span>
    </span>
  );
}

function getPortfolioVideoKind(video: any): "upload" | "link" | "empty" {
  if (!video) return "empty";
  if (video.sourceType === "upload" && video.file) return "upload";
  if (video.sourceType === "link" && video.linkUrl) return "link";
  return "empty";
}

function getUploadVideoSrc(video: any) {
  if (video?.sourceType !== "upload") return "";
  return video.file?.live_url || video.file?.local_url || "";
}

function getSafeHostname(url?: string) {
  if (!url) return "";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

export default function ProfileCompletionCard({
  currentStep,
}: {
  currentStep: number;
}) {
  const slice = useAppSelector((s) => s.signUpV2);
  const { percent } = useMemo(() => computeProfileCompletion(slice), [slice]);

  const animatedPercent = useAnimatedNumber(percent, 650);
  const emoji = useMemo(
    () => pickEmoji(percent, currentStep),
    [percent, currentStep],
  );

  const info = slice.SubmitApplicationInfo;
  const v2 = slice.v2Extras;
  const wardrobeImages = v2.wardrobeImages || [];
  const backgroundImages = v2.backgroundImages || [];

  const displayName = cleanOtherNoise(info.fullName || "");
  const displayAgeGender = [info.age, info.gender].filter(Boolean).join(" • ");
  const displayBodyType = cleanOtherNoise(info.bodyType || "");

  const displayLocation = getDisplayLocation([
    info.city,
    info.state,
    v2.pincode,
  ]);

  const skills = [...(v2.skillsMulti || []), ...(v2.customSkills || [])];

  const socials = getPrioritySocials(
    (v2.profileLinks || []).filter((s) => s.url && s.url.trim() !== ""),
  );
  const portfolioVideos = v2.portfolioVideos || [];
  const languages = v2.languages || [];
  const coreSetup = v2.coreSetup || [];

  const hasAnyData =
    v2.profilePhoto ||
    displayName ||
    displayAgeGender ||
    displayBodyType ||
    info.phoneNumber ||
    displayLocation ||
    skills.length > 0 ||
    socials.length > 0 ||
    portfolioVideos.length > 0 ||
    languages.length > 0 ||
    coreSetup.length > 0;

  return (
    <section className="max-w-[280px] rounded-[24px] border border-[#D8D8DD] bg-white px-4 py-[14px] shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      <div className="flex items-center justify-between gap-x-3">
        <div className="min-w-0">
          <p className="text-sm font-medium tracking-[-0.7px] text-[#060B13]">
            Your Profile is{" "}
            <span className="text-[#FF6600]">{animatedPercent}%</span> Complete
          </p>

          <div className="mt-2 h-[7px] w-[171px] overflow-hidden rounded-[21px] bg-[#FF66001A]">
            <div
              className="h-full rounded-[21px] bg-[#FF6600] transition-[width] duration-700 ease-out will-change-[width]"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFF4EC] text-xl">
          {emoji}
        </div>
      </div>

      <div
        className={`flex flex-col items-center justify-center ${
          hasAnyData ? "py-5" : "py-3"
        }`}
      >
        {v2.profilePhoto && (
          <div className="mb-3 h-[84px] w-[84px] overflow-hidden rounded-full bg-[#EDEDED] ring-4 ring-[#F7F7F8]">
            <img
              src={
                (v2.profilePhoto.live_url ||
                  v2.profilePhoto.local_url) as string
              }
              alt="profile"
              className="h-full w-full object-cover"
            />
          </div>
        )}

        {displayName && (
          <p className="text-center text-sm font-medium tracking-[-0.7px] text-[#121217]">
            {displayName}
          </p>
        )}

        {displayAgeGender && (
          <p className="mt-1 text-center text-[11px] font-medium tracking-[-0.7px] text-[#A6A6A6]">
            {displayAgeGender}
          </p>
        )}

        <div className="mt-2 flex w-full flex-col items-center gap-1">
          {displayBodyType && <InfoRow icon={null} text={displayBodyType} />}

          {info.phoneNumber && (
            <InfoRow icon={<PhoneIcon />} text={info.phoneNumber} />
          )}

          {displayLocation && (
            <InfoRow icon={<LocationIcon />} text={displayLocation} />
          )}
        </div>

        {skills.length > 0 && (
          <div className="mt-6 w-full text-center">
            <SectionTitle title="Skills" />
            <div className="flex flex-wrap justify-center gap-2">
              {skills.slice(0, 6).map((sk, index) => {
                const mapped = SKILL_MAP[sk];

                return (
                  <Chip key={`${sk}-${index}`} icon={mapped?.icon}>
                    {mapped?.label || cleanOtherNoise(sk)}
                  </Chip>
                );
              })}
            </div>
          </div>
        )}

        {socials.length > 0 && (
          <div className="mt-6 w-full text-center">
            <SectionTitle title="Socials" />
            <div className="flex flex-wrap justify-center gap-2">
              {socials.map((social) => (
                <div
                  key={social.id}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-[#F5F5F7] px-3 py-1.5 text-[10px] font-medium text-[#121217]"
                >
                  <span className="flex shrink-0 items-center">
                    {SOCIAL_ICON_MAP[social.platform]}
                  </span>

                  <span className="max-w-[86px] truncate">
                    {getSocialDisplayLabel(social)}
                  </span>

                  {social.followers != null ? (
                    <span className="shrink-0 text-[#6E6F76]">
                      {formatFollowers(social.followers)}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}

        {portfolioVideos.length > 0 && (
          <div className="mt-6 w-full">
            <SectionTitle title="Portfolio Videos" />
            <div className="flex flex-wrap justify-center gap-3">
              {portfolioVideos.map((video) => {
                const kind = getPortfolioVideoKind(video);
                const uploadSrc = getUploadVideoSrc(video);
                const hostname = getSafeHostname(video.linkUrl);

                return (
                  <div
                    key={video.id}
                    className="relative h-[96px] w-[72px] overflow-hidden rounded-[12px] border border-[#EDEDED] bg-[#FAFAFA]"
                  >
                    {kind === "upload" && uploadSrc ? (
                      <video
                        src={uploadSrc}
                        className="h-full w-full object-cover"
                        muted
                        playsInline
                      />
                    ) : kind === "link" && video.linkUrl ? (
                      <a
                        href={video.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-full w-full flex-col justify-between p-2 hover:bg-[#F3F4F6]"
                      >
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-sm">
                          🔗
                        </div>

                        <div>
                          <p className="line-clamp-2 text-[9px] font-semibold text-[#121217]">
                            {video.brandName || "Portfolio Link"}
                          </p>
                          <p className="mt-1 line-clamp-1 text-[8px] text-[#6E6F76]">
                            {hostname || "Open link"}
                          </p>
                        </div>
                      </a>
                    ) : (
                      <div className="flex h-full w-full items-end p-2">
                        <span className="line-clamp-2 text-[9px] font-medium text-[#6E6F76]">
                          {video.brandName || "Portfolio"}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {languages.length > 0 && (
          <div className="mt-6 w-full text-center">
            <SectionTitle title="Languages" />
            <div className="flex flex-wrap justify-center gap-2">
              {languages.slice(0, 4).map((lang) => (
                <Chip key={lang.id}>{cleanOtherNoise(lang.name)}</Chip>
              ))}
            </div>
          </div>
        )}

        {coreSetup.length > 0 && (
          <div className="mt-6 w-full text-center">
            <SectionTitle title="Core Setup" />
            <div className="flex flex-wrap justify-center gap-2">
              {coreSetup.slice(0, 4).map((item, index) => (
                <CoreSetupChip
                  key={`${item.type}-${index}`}
                  type={item.type}
                  details={item.details}
                />
              ))}
            </div>
          </div>
        )}

        {wardrobeImages.length > 0 && (
          <div className="mt-6 w-full">
            <SectionTitle title="Wardrobe" />
            <div className="flex flex-wrap justify-center gap-3">
              {wardrobeImages.map((img, index) => {
                const src = img?.live_url || img?.local_url || "";

                return (
                  <div
                    key={index}
                    className="h-[72px] w-[72px] overflow-hidden rounded-[12px] border border-[#EDEDED] bg-[#FAFAFA]"
                  >
                    {src ? (
                      <img
                        src={src}
                        alt="wardrobe"
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {backgroundImages.length > 0 && (
          <div className="mt-6 w-full">
            <SectionTitle title="Background" />
            <div className="flex flex-wrap justify-center gap-3">
              {backgroundImages.map((img, index) => {
                const src = img?.live_url || img?.local_url || "";

                return (
                  <div
                    key={index}
                    className="h-[72px] w-[72px] overflow-hidden rounded-[12px] border border-[#EDEDED] bg-[#FAFAFA]"
                  >
                    {src ? (
                      <img
                        src={src}
                        alt="wardrobe"
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
